import React, { useMemo, useState } from "react";
import { useList } from "@/lib/hooks";
import { PageHeader, Panel, EmptyState } from "@/components/common";
import { DatePicker } from "@/components/DatePicker";
import { Timer, Warning, EnvelopeSimple } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDur, toneOf, BREAK_TYPES } from "@/context/BreakContext";
import { TeamStatusBoard } from "@/components/TeamStatusBoard";

const today = () => new Date().toISOString().slice(0, 10);
const hm = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");

function PersonCard({ s }) {
  return (
    <Panel className="p-4" data-testid={`break-summary-${s.user_id}`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0"><p className="font-heading font-bold text-slate-900 truncate">{s.user_name}</p><p className="text-[11px] text-slate-400 capitalize">{s.role} · {s.count} break{s.count === 1 ? "" : "s"}</p></div>
        <p className="font-mono font-extrabold text-xl text-slate-900">{fmtDur(s.total_sec)}</p>
      </div>
      <div className="flex h-2 rounded-full overflow-hidden bg-slate-100 mt-3">
        {BREAK_TYPES.map((b) => s.by_type[b.type] ? <span key={b.type} style={{ width: `${(s.by_type[b.type] / s.total_sec) * 100}%`, background: b.tone }} title={`${b.type} ${fmtDur(s.by_type[b.type])}`} /> : null)}
      </div>
      {s.overruns > 0 && <p className="text-xs font-semibold text-red-600 mt-2 flex items-center gap-1"><Warning size={13} weight="fill" />{s.overruns} overrun{s.overruns > 1 ? "s" : ""} · {fmtDur(s.overrun_sec)} over limit</p>}
    </Panel>
  );
}

export default function BreakLog() {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const { data, isLoading } = useList(["break-log", from, to], `/breaks/log?date_from=${from}&date_to=${to}`, { refetchInterval: 30000 });
  const breaks = useMemo(() => data?.breaks || [], [data]);
  const total = breaks.reduce((s, b) => s + b.duration_sec, 0);
  const overruns = breaks.filter((b) => b.overrun_sec > 0).length;
  const [sending, setSending] = useState(false);
  const sendReport = async () => {
    setSending(true);
    try { const { data: r } = await api.post("/breaks/weekly-report/send"); toast.success(r.message); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSending(false); }
  };
  const th = "px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400";
  return (
    <div>
      <PageHeader title="Break Tracker" subtitle="Live team status and every break your team reports. A weekly report is emailed to admins every Monday at 10am IST."
        actions={<button onClick={sendReport} disabled={sending} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50" data-testid="breaklog-send-report"><EnvelopeSimple size={16} />{sending ? "Sending…" : "Email Last Week's Report"}</button>} />
      <TeamStatusBoard />
      <div className="flex flex-wrap items-end gap-3 my-5">
        <div className="w-44"><p className="text-xs font-semibold text-slate-500 mb-1">From</p><DatePicker value={from} onChange={setFrom} className="bg-white border-slate-300" testId="breaklog-from" /></div>
        <div className="w-44"><p className="text-xs font-semibold text-slate-500 mb-1">To</p><DatePicker value={to} onChange={setTo} className="bg-white border-slate-300" testId="breaklog-to" /></div>
        <div className="flex gap-6 ml-auto text-sm">
          <span>Breaks <b className="font-mono text-slate-900" data-testid="breaklog-count">{breaks.length}</b></span>
          <span>Total time <b className="font-mono text-slate-900" data-testid="breaklog-total">{fmtDur(total)}</b></span>
          <span>Overruns <b className="font-mono text-red-600" data-testid="breaklog-overruns">{overruns}</b></span>
        </div>
      </div>
      {isLoading ? <Panel><p className="p-6 text-slate-400">Loading…</p></Panel> : breaks.length === 0 ? (
        <Panel><EmptyState icon={Timer} title="No Breaks in This Range" hint="Breaks reported by admins, sales agents and dispatchers appear here." /></Panel>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">{data.summary.map((s) => <PersonCard key={s.user_id} s={s} />)}</div>
          <Panel className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="breaklog-table">
              <thead className="border-b border-slate-200 bg-slate-50/60"><tr>{["Person", "Break", "Date", "Start", "End", "Duration", "Limit", "Status"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {breaks.map((b) => (
                  <tr key={b.id} className={b.overrun_sec > 0 ? "bg-red-50/40" : ""} data-testid={`breaklog-row-${b.id}`}>
                    <td className="px-4 py-2.5 font-semibold text-slate-900">{b.user_name}<span className="block text-[11px] font-normal text-slate-400 capitalize">{b.role}</span></td>
                    <td className="px-4 py-2.5"><span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: toneOf(b.type) }}><span className="w-2 h-2 rounded-full" style={{ background: toneOf(b.type) }} />{b.type}</span></td>
                    <td className="px-4 py-2.5 text-slate-600">{b.date}</td>
                    <td className="px-4 py-2.5 font-mono text-slate-600">{hm(b.started_at)}</td>
                    <td className="px-4 py-2.5 font-mono text-slate-600">{b.ended_at ? hm(b.ended_at) : <span className="text-amber-600 font-semibold">On break</span>}</td>
                    <td className="px-4 py-2.5 font-mono font-semibold text-slate-900">{fmtDur(b.duration_sec)}</td>
                    <td className="px-4 py-2.5 text-slate-500">{b.limit_min ? `${b.limit_min} min` : "No limit"}</td>
                    <td className="px-4 py-2.5">{b.overrun_sec > 0 ? <span className="text-xs font-bold text-red-600">Over by {fmtDur(b.overrun_sec)}</span> : <span className="text-xs font-semibold text-emerald-600">Within limit</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </>
      )}
    </div>
  );
}
